// LeadDetailRight — right column of the Ringy-style Lead Detail:
//   1. Create Appointment button
//   2. Notes summary chip — shows count + last-updated relative time. The
//      composer + full history moved to the center-column Notes tab in
//      August 2026 so agents get a real workspace instead of a cramped
//      right-rail textarea. This chip is just an at-a-glance indicator.
//   3. AI Suggestions card → GET /leads/:id/ai-suggestions (1-3 next actions)

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, Lightbulb, Loader2, CalendarPlus, StickyNote } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../../lib/api.js';
import { fmtFromNow } from '../../lib/format.js';
import ScheduleAppointmentModal from '../ScheduleAppointmentModal.jsx';
import useBookEvent from '../../hooks/useBookEvent.js';
import SendToIntegrityButton from '../integrity/SendToIntegrityButton.jsx';
import IntegrityHealthPanel from '../integrity/IntegrityHealthPanel.jsx';

function AiSuggestions({ leadId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['lead-ai-suggestions', leadId],
    queryFn: () => apiGet(`/leads/${leadId}/ai-suggestions`),
    enabled: !!leadId,
  });
  const suggestions = data?.suggestions || [];

  return (
    <div className="rounded-lg bg-civix-panel border border-civix-border p-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-white/85 mb-2">
        <Sparkles className="w-4 h-4 text-civix-purpleSoft" /> AI Suggestions
      </div>
      {isLoading && (
        <p className="text-[11px] text-white/40 flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
        </p>
      )}
      <ul className="space-y-2">
        {suggestions.map((s, i) => (
          <li key={i} className="rounded-lg bg-civix-bg border border-civix-border px-2.5 py-2">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[11px] font-semibold text-white/85">{s.action}</div>
                <div className="text-[10px] text-white/55 mt-0.5">{s.reason}</div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function LeadDetailRight({ leadId, lead, onChange }) {
  const [showSchedule, setShowSchedule] = useState(false);
  const notes = lead?.notes || [];
  const lastNote = notes[notes.length - 1];

  // Book the event (calendar + disposition + any notifications the agent switched on).
  const bookAppointment = useBookEvent(leadId, () => { setShowSchedule(false); onChange?.(); });

  return (
    <div className="space-y-4">
      <button
        onClick={() => setShowSchedule(true)}
        disabled={!lead}
        className="btn-primary w-full flex items-center justify-center gap-2 text-sm disabled:opacity-50"
      >
        <CalendarPlus className="w-4 h-4" /> Create Appointment
      </button>

      {/* Send this lead to Integrity Connect under the agent's NPN. Renders a
          no-credential help state, a linked-status card, or the push CTA
          depending on state — all wired to /api/integrity/*. */}
      <SendToIntegrityButton leadId={leadId} lead={lead} />
      <IntegrityHealthPanel leadId={leadId} />

      {/* Compact Notes summary. The real composer + history live in the */}
      {/* center-column Notes tab (default tab when the lead opens). */}
      <div className="rounded-lg bg-civix-panel border border-civix-border p-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-white/85 mb-1">
          <StickyNote className="w-4 h-4 text-civix-purpleSoft" /> Notes
        </div>
        {notes.length === 0 ? (
          <div className="text-[11px] text-white/40">
            No notes yet — open the <span className="text-white/70">Notes</span> tab to add one.
          </div>
        ) : (
          <>
            <div className="text-[11px] text-white/55">
              {notes.length} note{notes.length === 1 ? '' : 's'} · last {fmtFromNow(lastNote?.createdAt)}
            </div>
            <div className="mt-1.5 text-[11px] text-white/75 line-clamp-2 whitespace-pre-wrap wrap-break-word">
              {lastNote?.body}
            </div>
          </>
        )}
      </div>

      <AiSuggestions leadId={leadId} />

      {showSchedule && lead && (
        <ScheduleAppointmentModal
          lead={lead}
          onCancel={() => setShowSchedule(false)}
          onSubmit={bookAppointment}
        />
      )}
    </div>
  );
}
